import React, { useState, useEffect, useRef, ReactNode } from "react";
import { GoChevronDown } from "react-icons/go";
import classNames from "classnames";
import { FormikErrors, FormikTouched } from "formik";
import { twMerge } from "tailwind-merge";
import { Option } from "../../types";

interface DropdownProps<T> {
  id: string;
  label?: string;
  placeholder?: string;
  options?: Option[];
  value?: string;
  className?: string;
  isFetching?: boolean;
  errors?: FormikErrors<T>;
  touched?: FormikTouched<T>;
  setFieldValue: (field: string, value: string) => void;
  setFieldTouched?: (field: string, touched: boolean) => void;
}

const Dropdown: React.FC<DropdownProps<any>> = ({
  id,
  placeholder,
  options = [],
  value,
  label,
  className,
  isFetching = false,
  errors = {},
  touched = {},
  setFieldValue,
  setFieldTouched,
}) => {
  // hooks
  const [isOpen, setIsOpen] = useState(false);
  const divEl = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (!divEl.current) return;
      if (!divEl.current.contains(event.target as Node)) setIsOpen(false);
    };

    document.addEventListener("click", handler, false);

    return () => {
      document.removeEventListener("click", handler);
    };
  }, []);

  useEffect(() => {
    if (options?.length && !options.some((option) => option.value === value)) {
      setFieldValue(id, options[0].value);
    }
  }, [options, id, value, setFieldValue]);

  // classes
  const dropdownClasses = classNames(
    className,
    "relative cursor-pointer rounded"
  );
  const inputContainerClasses = twMerge(
    classNames(
      "flex items-center bg-white rounded p-2.5 space-evenly",
      {
        "border border-red-700": errors[id] && touched[id],
      },
      className
    )
  );
  const errorMessageClasses = classNames("text-sm text-red-700");

  // event handlers
  const handleClick = () => {
    setIsOpen((currentIsOpen) => !currentIsOpen);
  };

  const handleOptionClick = (selectedOption: Option) => {
    setIsOpen(false);
    setFieldTouched && setFieldTouched(id, true);
    setFieldValue(id, selectedOption.value);
  };

  // renders
  const renderedOptions = options.map((optionElem) => (
    <div
      key={optionElem.value}
      className="flex gap-2 items-center hover:bg-gray-200 rounded cursor-pointer p-1"
      onClick={() => handleOptionClick(optionElem)}
    >
      <div>{optionElem.label}</div>
    </div>
  ));

  return (
    <div
      ref={divEl}
      className={dropdownClasses}
    >
      <div className={inputContainerClasses}>
        {label && (
          <div className="font-semibold whitespace-nowrap"> {label} </div>
        )}
        <div
          className="w-full flex items-center gap-2 px-2"
          onClick={handleClick}
        >
          <input
            value={options.find((opt) => opt.value === value)?.label || ""}
            placeholder={placeholder}
            className="w-full bg-transparent cursor-pointer"
            readOnly
          />
          <GoChevronDown className="text-lg" />
        </div>
      </div>

      {isOpen &&
        (isFetching ? (
          <div>loading...</div>
        ) : (
          <div className="absolute top-full w-full border bg-white z-30 p-1 my-1 max-h-80 overflow-y-auto">
            {" "}
            {renderedOptions}{" "}
          </div>
        ))}

      <div className="px-2">
        {errors[id] && touched[id] && (
          <div className={errorMessageClasses}> {errors[id] as ReactNode} </div>
        )}
      </div>
    </div>
  );
};

export default Dropdown;
